/* global window */

const TEXT_NODE = 3
const ELEMENT_NODE = 1

const MARGIN = 48
const BODY_FONT_SIZE = 11
const BODY_LINE_HEIGHT = 15
const LIST_INDENT = 18

const HEADING_STYLES = {
  H1: { fontSize: 18, lineHeight: 24, spaceBefore: 6, spaceAfter: 8 },
  H2: { fontSize: 15, lineHeight: 20, spaceBefore: 10, spaceAfter: 6 },
  H3: { fontSize: 13, lineHeight: 18, spaceBefore: 8, spaceAfter: 6 },
  H4: { fontSize: 12, lineHeight: 16, spaceBefore: 6, spaceAfter: 4 },
  H5: { fontSize: 11, lineHeight: 15, spaceBefore: 6, spaceAfter: 4 },
  H6: { fontSize: 11, lineHeight: 15, spaceBefore: 6, spaceAfter: 4 }
}

const BLOCK_TAGS = {
  P: true,
  H1: true,
  H2: true,
  H3: true,
  H4: true,
  H5: true,
  H6: true,
  UL: true,
  OL: true,
  LI: true,
  TABLE: true,
  THEAD: true,
  TBODY: true,
  TFOOT: true,
  TR: true,
  BLOCKQUOTE: true,
  PRE: true,
  DIV: true,
  SECTION: true,
  ARTICLE: true,
  HEADER: true,
  FOOTER: true,
  HR: true,
  BR: true
}

const fontStyleFromFlags = (bold, italic) => {
  if (bold && italic) {
    return 'bolditalic'
  }

  if (bold) {
    return 'bold'
  }

  if (italic) {
    return 'italic'
  }

  return 'normal'
}

const hasBlockChild = node => Array.from(node.childNodes).some(
  child => child.nodeType === ELEMENT_NODE && BLOCK_TAGS[child.nodeName]
)

const collectInlineSegments = (node, inherited = { bold: false, italic: false, link: null }) => {
  const segments = []

  const walk = (current, style) => {
    if (current.nodeType === TEXT_NODE) {
      const text = current.textContent.replace(/\s+/g, ' ')

      if (text) {
        segments.push({
          text,
          bold: style.bold,
          italic: style.italic,
          link: style.link
        })
      }

      return
    }

    if (current.nodeType !== ELEMENT_NODE) {
      return
    }

    const tag = current.nodeName

    if (tag === 'SCRIPT' || tag === 'STYLE') {
      return
    }

    if (tag === 'BR') {
      segments.push({
        text: '\n',
        bold: style.bold,
        italic: style.italic,
        link: style.link
      })

      return
    }

    const nextStyle = {
      bold: style.bold || tag === 'STRONG' || tag === 'B',
      italic: style.italic || tag === 'EM' || tag === 'I',
      link: tag === 'A' ? current.getAttribute('href') : style.link
    }

    Array.from(current.childNodes).forEach(child => walk(child, nextStyle))
  }

  walk(node, inherited)

  return segments
}

export const renderHtmlToPdf = (pdf, html, options = {}) => {
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const contentWidth = pageWidth - (MARGIN * 2)
  let y = MARGIN

  const setFont = (style, fontSize) => {
    pdf.setFont('helvetica', style)
    pdf.setFontSize(fontSize)
  }

  const ensureSpace = height => {
    if (y + height <= pageHeight - MARGIN) {
      return
    }

    pdf.addPage()
    y = MARGIN
  }

  const writeSegments = (segments, {
    fontSize = BODY_FONT_SIZE,
    lineHeight = BODY_LINE_HEIGHT,
    indent = 0
  } = {}) => {
    const startX = MARGIN + indent
    const maxX = MARGIN + contentWidth
    let cursorX = startX
    let lineStart = true

    const breakLine = () => {
      y += lineHeight
      ensureSpace(lineHeight)
      cursorX = startX
      lineStart = true
    }

    ensureSpace(lineHeight)

    segments.forEach(segment => {
      setFont(fontStyleFromFlags(segment.bold, segment.italic), fontSize)

      const tokens = segment.text.split(/(\s+|\n)/)

      tokens.forEach(token => {
        if (!token) {
          return
        }

        if (token === '\n') {
          breakLine()

          return
        }

        const isSpace = /^\s+$/.test(token)

        if (isSpace && lineStart) {
          return
        }

        const width = pdf.getTextWidth(token)

        if (!isSpace && cursorX > startX && cursorX + width > maxX) {
          breakLine()
          setFont(fontStyleFromFlags(segment.bold, segment.italic), fontSize)
        }

        const drawX = cursorX
        const drawY = y + fontSize

        if (segment.link) {
          pdf.textWithLink(token, drawX, drawY, { url: segment.link })
        } else {
          pdf.text(token, drawX, drawY)
        }

        cursorX += width
        lineStart = false
      })
    })

    y += lineHeight
  }

  const writeBlock = (node, style = {}) => {
    const segments = collectInlineSegments(node, {
      bold: Boolean(style.bold),
      italic: Boolean(style.italic),
      link: null
    })

    if (!segments.length) {
      y += style.spaceAfter || 4

      return
    }

    if (style.spaceBefore) {
      y += style.spaceBefore
    }

    writeSegments(segments, {
      fontSize: style.fontSize || BODY_FONT_SIZE,
      lineHeight: style.lineHeight || BODY_LINE_HEIGHT,
      indent: style.indent || 0
    })

    y += style.spaceAfter || 4
  }

  const renderHeader = () => {
    if (!options.showAiLabel) {
      return
    }

    const fontSize = 16
    const lineHeight = 22

    ensureSpace(lineHeight)

    const radius = 8
    const centerX = MARGIN + radius
    const centerY = y + radius

    pdf.setFillColor(158, 158, 158)
    pdf.circle(centerX, centerY, radius, 'F')
    pdf.setTextColor(255, 255, 255)
    setFont('bold', 8)
    pdf.text('AI', centerX, centerY + 3, { align: 'center' })
    pdf.setTextColor(0, 0, 0)

    if (options.title) {
      setFont('bold', fontSize)
      pdf.text(options.title, MARGIN + (radius * 2) + 8, y + fontSize - 2)
    }

    y += lineHeight + 4
    pdf.setDrawColor(224, 224, 224)
    pdf.setLineWidth(0.5)
    pdf.line(MARGIN, y, pageWidth - MARGIN, y)
    y += 14
  }

  const renderTable = node => {
    const rows = []

    node.querySelectorAll('tr').forEach(tr => {
      const cells = []

      tr.querySelectorAll('th, td').forEach(cell => {
        cells.push({
          text: (cell.textContent || '').replace(/\s+/g, ' ').trim(),
          isHeader: cell.nodeName === 'TH' || (tr.parentNode && tr.parentNode.nodeName === 'THEAD')
        })
      })

      if (cells.length) {
        rows.push(cells)
      }
    })

    if (!rows.length) {
      return
    }

    const colCount = rows.reduce((max, row) => Math.max(max, row.length), 1)
    const colWidth = contentWidth / colCount
    const cellPadding = 5
    const fontSize = 9
    const lineHeight = 12

    rows.forEach(row => {
      const cellLines = row.map(cell => {
        setFont(cell.isHeader ? 'bold' : 'normal', fontSize)
        const wrapped = pdf.splitTextToSize(cell.text, Math.max(colWidth - (cellPadding * 2), 20))

        return wrapped.length ? wrapped : ['']
      })
      const rowHeight = Math.max(
        ...cellLines.map(lines => (lines.length * lineHeight) + (cellPadding * 2))
      )

      ensureSpace(rowHeight)

      row.forEach((cell, colIndex) => {
        const x = MARGIN + (colIndex * colWidth)

        pdf.setDrawColor(189, 189, 189)

        if (cell.isHeader) {
          pdf.setFillColor(245, 245, 245)
          pdf.rect(x, y, colWidth, rowHeight, 'FD')
        } else {
          pdf.rect(x, y, colWidth, rowHeight)
        }

        setFont(cell.isHeader ? 'bold' : 'normal', fontSize)
        pdf.setTextColor(0, 0, 0)
        cellLines[colIndex].forEach((line, lineIndex) => {
          pdf.text(
            line,
            x + cellPadding,
            y + cellPadding + fontSize + (lineIndex * lineHeight)
          )
        })
      })

      y += rowHeight
    })

    y += 8
  }

  const renderNode = (node, context = { indent: 0 }) => {
    if (node.nodeType === TEXT_NODE) {
      if (!node.textContent.trim()) {
        return
      }

      writeSegments(
        [{
          text: node.textContent.replace(/\s+/g, ' '),
          bold: false,
          italic: false,
          link: null
        }],
        { indent: context.indent }
      )

      return
    }

    if (node.nodeType !== ELEMENT_NODE) {
      return
    }

    const tag = node.nodeName

    if (tag === 'SCRIPT' || tag === 'STYLE') {
      return
    }

    if (HEADING_STYLES[tag]) {
      writeBlock(node, {
        ...HEADING_STYLES[tag],
        bold: true,
        indent: context.indent
      })

      return
    }

    if (tag === 'P') {
      writeBlock(node, { indent: context.indent, spaceAfter: 6 })

      return
    }

    if (tag === 'BR') {
      y += BODY_LINE_HEIGHT

      return
    }

    if (tag === 'HR') {
      y += 8
      ensureSpace(4)
      pdf.setDrawColor(224, 224, 224)
      pdf.setLineWidth(0.5)
      pdf.line(MARGIN + context.indent, y, pageWidth - MARGIN, y)
      y += 10

      return
    }

    if (tag === 'BLOCKQUOTE') {
      writeBlock(node, {
        indent: context.indent + LIST_INDENT,
        italic: true,
        spaceBefore: 4,
        spaceAfter: 6
      })

      return
    }

    if (tag === 'PRE') {
      const text = node.textContent.replace(/\t/g, '  ')
      setFont('normal', 9)
      const lines = pdf.splitTextToSize(text, contentWidth - context.indent)

      lines.forEach(line => {
        ensureSpace(13)
        pdf.setFont('courier', 'normal')
        pdf.setFontSize(9)
        pdf.text(line, MARGIN + context.indent, y + 9)
        y += 13
      })

      y += 6

      return
    }

    if (tag === 'TABLE') {
      renderTable(node)

      return
    }

    if (tag === 'UL' || tag === 'OL') {
      let index = 0
      const ordered = tag === 'OL'

      Array.from(node.childNodes).forEach(child => {
        if (child.nodeName !== 'LI') {
          return
        }

        index += 1
        const marker = ordered ? `${index}. ` : '• '
        const indent = context.indent + LIST_INDENT

        setFont('normal', BODY_FONT_SIZE)
        ensureSpace(BODY_LINE_HEIGHT)
        pdf.text(marker, MARGIN + context.indent, y + BODY_FONT_SIZE)

        if (hasBlockChild(child)) {
          y += BODY_LINE_HEIGHT
          Array.from(child.childNodes).forEach(grandChild => {
            renderNode(grandChild, { indent })
          })
        } else {
          writeBlock(child, { indent, spaceAfter: 2 })
        }
      })

      y += 4

      return
    }

    if (hasBlockChild(node) || tag === 'DIV' || tag === 'BODY' || tag === 'SECTION' || tag === 'ARTICLE') {
      Array.from(node.childNodes).forEach(child => {
        renderNode(child, context)
      })

      return
    }

    writeBlock(node, { indent: context.indent, spaceAfter: 4 })
  }

  pdf.setTextColor(0, 0, 0)
  renderHeader()

  const parser = new window.DOMParser()
  const doc = parser.parseFromString(`<div>${html || ''}</div>`, 'text/html')

  renderNode(doc.body)
}

export const htmlToPdf = (html, filename, options = {}) => (
  import('jspdf').then(({ jsPDF }) => {
    const pdf = new jsPDF({
      unit: 'pt',
      format: 'a4',
      orientation: 'portrait'
    })

    renderHtmlToPdf(pdf, html, options)
    pdf.save(filename)
  })
)
